/**
 * Invitation / account-activation flow (email link, JWT, password set).
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  signInWithEmailLink,
  updatePassword,
  confirmPasswordReset,
  ConfirmationResult,
  isSignInWithEmailLink,
} from 'firebase/auth';
import { auth, initRecaptchaVerifier, cleanupRecaptcha } from '@/config/firebase';
import {
  sendPhoneOtp,
  verifyPhoneOtp,
  activateAccount as apiActivateAccount,
} from '@/services/auth.service';
import { getErrorMessage } from '@/utils/errors-utils';
import { formatPhoneNumber, validatePassword } from '@/utils/auth-validation-utils';
import { getTutorMobileLoginUrl } from '@/utils/guest-meeting-join-utils';
import { toast } from '@/components/ui/toast';

/** Invitation / account-activation flow (email link, JWT, password set, phone OTP). */
export const useInvitationSetup = () => {
  const [searchParams] = useSearchParams();

  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  const phone = searchParams.get('phone');
  const emailParam = searchParams.get('email');
  const roleParam = searchParams.get('role');
  const roleGroupParam = searchParams.get('roleGroup');
  const token = searchParams.get('token');

  // Firebase email links use mode=signIn; mode=setup is legacy; mode=activate is the JWT (14d) flow.
  // mode=reset-password is for password reset with Firebase oobCode.
  const isSetupMode =
    mode === 'setup' ||
    mode === 'signIn' ||
    mode === 'phone-setup' ||
    mode === 'activate' ||
    mode === 'reset-password';
  const isEmailSetup = (mode === 'setup' || mode === 'signIn') && !!oobCode;
  const isPhoneSetup = mode === 'phone-setup' && !!phone;
  const isActivateSetup = mode === 'activate' && !!token;
  const isResetMode = mode === 'reset-password' && !!oobCode;

  // Tutors go to the mobile app after activation (`role` or `roleGroup`).
  const isTutorInvite = roleParam === 'tutor' || roleGroupParam === 'tutors';

  const redirectAfterSetup = (resolvedRoleGroup?: string) => {
    const isTutor = resolvedRoleGroup === 'tutors' || isTutorInvite;
    if (isTutor) {
      const url = getTutorMobileLoginUrl();
      if (!url) {
        toast.error(getErrorMessage('mobileRedirect'));
        return;
      }
      globalThis.location.href = url;
    } else {
      globalThis.location.href = '/login';
    }
  };

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [invitationEmail, setInvitationEmail] = useState<string | null>(emailParam);

  // Email from the URL first; else Firebase `emailForSignIn` in localStorage.
  useEffect(() => {
    if (isEmailSetup) {
      if (emailParam) {
        setInvitationEmail(emailParam);
        return;
      }


      if (isSignInWithEmailLink(auth, globalThis.location.href)) {
        const storedEmail = globalThis.localStorage.getItem('emailForSignIn');
        if (storedEmail) {
          setInvitationEmail(storedEmail);
        }
      }
    }
  }, [isEmailSetup, emailParam]);

  const setupPassword = async (
    email: string,
    password: string,
    confirmPassword: string,
  ): Promise<void> => {
    setError(undefined);

    if (password !== confirmPassword) {
      const errorMsg = getErrorMessage('passwordsMismatch');
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      const invalidRules = passwordValidation.rules
        .filter((rule) => !rule.valid)
        .map((rule) => rule.label);
      const errorMsg = getErrorMessage({
        code: 'passwordRulesFailed',
        rules: invalidRules.join(', '),
      });
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    setIsLoading(true);

    try {
      const userCredential = await signInWithEmailLink(auth, email, globalThis.location.href);

      const userEmail = userCredential.user.email;
      if (userEmail) {
        setInvitationEmail(userEmail);
      }

      if (userCredential.user) {
        await updatePassword(userCredential.user, password);
      }

      // Sign out so the user logs in again with the new password.
      await auth.signOut();

      globalThis.localStorage.removeItem('emailForSignIn');

      toast.success('Votre compte a été activé ! Vous pouvez maintenant vous connecter.');

      setTimeout(redirectAfterSetup, 2000);
    } catch (err) {
      const errorMsg = getErrorMessage(err);
      setError(errorMsg);
      toast.error(errorMsg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Activate account from an email invitation token (mode=activate). Sets the password via the
   * backend, then redirects. The link is valid 14 days; expired/invalid tokens surface a clear
   * message from the API.
   */
  const activateAccount = async (password: string, confirmPassword: string): Promise<void> => {
    setError(undefined);

    if (password !== confirmPassword) {
      const errorMsg = getErrorMessage('passwordsMismatch');
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      const invalidRules = passwordValidation.rules
        .filter((rule) => !rule.valid)
        .map((rule) => rule.label);
      const errorMsg = getErrorMessage({
        code: 'passwordRulesFailed',
        rules: invalidRules.join(', '),
      });
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    if (!token) {
      const errorMsg = getErrorMessage('invalidActivationLink');
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    setIsLoading(true);

    try {
      const result = await apiActivateAccount(token, password);
      toast.success('Votre compte a été activé ! Vous pouvez maintenant vous connecter.');
      const resolvedRoleGroup = result.roleGroup ?? roleGroupParam;
      setTimeout(() => redirectAfterSetup(resolvedRoleGroup ?? undefined), 2000);
    } catch (err: unknown) {
      const errorMsg = getErrorMessage(err);
      setError(errorMsg);
      toast.error(errorMsg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const verifyPhoneSetup = async (
    confirmationResult: ConfirmationResult,
    code: string,
  ): Promise<void> => {
    setError(undefined);
    setIsLoading(true);

    try {
      await verifyPhoneOtp(confirmationResult, code);

      toast.success('Votre compte a été activé ! Redirection vers le login...');

      globalThis.setTimeout(redirectAfterSetup, 2000);
    } catch (err) {
      const errorMsg = getErrorMessage(err);
      setError(errorMsg);
      toast.error(errorMsg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const sendPhoneSetupOtp = async (phoneNumber: string): Promise<ConfirmationResult> => {
    setError(undefined);
    setIsLoading(true);

    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      const verifier = initRecaptchaVerifier();
      const confirmationResult = await sendPhoneOtp(formattedPhone, verifier);

      return confirmationResult;
    } catch (err) {
      const errorMsg = getErrorMessage(err);
      setError(errorMsg);
      toast.error(errorMsg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const cleanup = (verifier: any) => {
    cleanupRecaptcha(verifier);
  };

  /**
   * Reset password using Firebase oobCode (mode=reset-password).
   * Validates password, confirms reset via Firebase, then redirects to login.
   */
  const resetPassword = async (password: string, confirmPassword: string): Promise<void> => {
    setError(undefined);

    if (password !== confirmPassword) {
      const errorMsg = getErrorMessage('passwordsMismatch');
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      const invalidRules = passwordValidation.rules
        .filter((rule) => !rule.valid)
        .map((rule) => rule.label);
      const errorMsg = getErrorMessage({
        code: 'passwordRulesFailed',
        rules: invalidRules.join(', '),
      });
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    if (!oobCode) {
      const errorMsg = getErrorMessage('invalidResetLink');
      setError(errorMsg);
      throw new Error(errorMsg);
    }

    setIsLoading(true);

    try {
      await confirmPasswordReset(auth, oobCode, password);
      toast.success('Votre mot de passe a été réinitialisé ! Vous pouvez maintenant vous connecter.');
      setTimeout(() => {
        globalThis.location.href = '/login';
      }, 2000);
    } catch (err: unknown) {
      const errorMsg = getErrorMessage(err);
      setError(errorMsg);
      toast.error(errorMsg);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isSetupMode,
    isEmailSetup,
    isPhoneSetup,
    isActivateSetup,
    isResetMode,
    oobCode,
    phone,
    invitationEmail,
    isLoading,
    error,
    setupPassword,
    activateAccount,
    resetPassword,
    verifyPhoneSetup,
    sendPhoneSetupOtp,
    cleanup,
  };
};
